
import { useState, useMemo, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Hotel as HotelType, Room as RoomType } from "@/types/hotel";
import type { PackageData } from '@/pages/CreatePackage';
import { DateRange } from "react-day-picker";
import { format, differenceInCalendarDays } from "date-fns";
import { CalendarIcon, Hotel, Plus, Star } from "lucide-react";

interface HotelStayDetailsProps {
  selectedHotel: HotelType;
  rooms: RoomType[];
  onAddHotelToPackage: (hotelToAdd: PackageData['hotels'][0]) => void;
}

export const HotelStayDetails = ({ selectedHotel, rooms, onAddHotelToPackage }: HotelStayDetailsProps) => {
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  useEffect(() => {
    setSelectedRoomId(null);
    setDateRange(undefined);
  }, [selectedHotel]);

  const availableRooms = useMemo(() => {
    return selectedHotel ? rooms.filter(room => room.hotel_id === selectedHotel.id) : [];
  }, [rooms, selectedHotel]);

  const selectedRoom = useMemo(() => {
    return rooms.find(room => room.id === selectedRoomId) || null;
  }, [rooms, selectedRoomId]);

  const nights = useMemo(() => {
    if (dateRange?.from && dateRange?.to) {
      return differenceInCalendarDays(dateRange.to, dateRange.from);
    }
    return 0;
  }, [dateRange]);

  const totalPrice = useMemo(() => {
    if (selectedRoom && nights > 0) {
      // TODO: Implement weekly pricing logic
      return nights * (selectedRoom.selling_price || 0);
    }
    return 0;
  }, [selectedRoom, nights]);

  const handleAddHotel = () => {
    if (!selectedHotel || !selectedRoom || !dateRange?.from || !dateRange?.to || nights <= 0) {
      return;
    }

    const newHotelStay: PackageData['hotels'][0] = {
      name: selectedHotel.name_ar,
      location: `${selectedHotel.city}, ${selectedHotel.country}`,
      checkIn: format(dateRange.from, 'yyyy-MM-dd'),
      checkOut: format(dateRange.to, 'yyyy-MM-dd'),
      roomType: selectedRoom.name,
      nights: nights,
      pricePerNight: selectedRoom.selling_price || 0,
    };

    onAddHotelToPackage(newHotelStay);
  };

  return (
    <div className="p-4 border rounded-lg bg-gray-50 grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="col-span-1 md:col-span-3 flex items-start gap-4">
         {selectedHotel.image_url ? (
              <img src={selectedHotel.image_url} alt={selectedHotel.name_ar} className="w-24 h-24 object-cover rounded-md border flex-shrink-0"/>
          ) : (
              <div className="w-24 h-24 bg-gray-200 rounded-md flex items-center justify-center flex-shrink-0">
                  <Hotel className="w-10 h-10 text-gray-400" />
              </div>
          )}
        <div>
          <h4 className="font-bold text-xl">{selectedHotel.name_ar}</h4>
          <p className="text-sm text-gray-500">{selectedHotel.city}, {selectedHotel.country}</p>
          <div className="flex items-center gap-1 mt-2">
            {Array.from({ length: selectedHotel.stars }).map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
          </div>
        </div>
      </div>
      <div>
        <Label>الغرفة</Label>
        <Select value={selectedRoomId || ''} onValueChange={setSelectedRoomId}>
          <SelectTrigger><SelectValue placeholder="اختر غرفة" /></SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>الغرف المتاحة</SelectLabel>
              {availableRooms.map(room => (
                <SelectItem key={room.id} value={room.id}>
                  {room.name} - {room.selling_price?.toLocaleString()} SAR/الليلة
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>فترة الإقامة</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "w-full justify-start text-right font-normal",
                !dateRange && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="ml-2 h-4 w-4" />
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {format(dateRange.from, "LLL dd, y")} -{" "}
                    {format(dateRange.to, "LLL dd, y")}
                  </>
                ) : (
                  format(dateRange.from, "LLL dd, y")
                )
              ) : (
                <span>اختر التواريخ</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={dateRange?.from}
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
              disabled={{ before: new Date() }}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="col-span-1 md:col-span-3 flex items-end justify-between bg-white p-4 rounded-md">
        <div>
          <h5 className="font-semibold">ملخص الحجز</h5>
          <div className="flex gap-6 text-sm">
            <p>عدد الليالي: <Badge variant="secondary">{nights}</Badge></p>
            <p>السعر الإجمالي: <Badge className="bg-blue-100 text-blue-800">{totalPrice.toLocaleString()} SAR</Badge></p>
          </div>
        </div>
        <Button onClick={handleAddHotel} disabled={!selectedRoom || nights <= 0}>
          <Plus className="w-4 h-4 ml-2" />
          إضافة الفندق للعرض
        </Button>
      </div>
    </div>
  );
};
